import { Link } from "react-router-dom";
import { Lock, ShieldCheck, Sparkles, CalendarDays, ArrowRight, Check, BadgeCheck } from "lucide-react";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { homeTrack } from "../shared";
import { PremiumStoryCard } from "./PremiumStoryCard";
import { RegulationRadarCard } from "./RegulationRadarCard";

const BENEFITS = [
  { icon: ShieldCheck, tone: "text-[#A78BFA]", label: ["Ad-free", "experience"] },
  { icon: Sparkles, tone: "text-[#22D3EE]", label: ["Exclusive", "intel"] },
  { icon: CalendarDays, tone: "text-[#34D399]", label: ["Weekly", "briefings"] },
];

const PremiumIntro = ({ isPremium, loggedIn }: { isPremium: boolean; loggedIn: boolean }) => (
  <div className="relative flex flex-col min-w-0" data-testid="premium-intro">
    <svg aria-hidden className="absolute bottom-10 -left-4 w-full max-w-[380px] opacity-[0.35] pointer-events-none hidden xl:block"
      viewBox="0 0 380 140" fill="none">
      <path d="M0 120 C 90 118, 140 60, 210 58 S 340 88, 380 30" stroke="#7C3AED" strokeOpacity="0.35" strokeWidth="1" />
      <path d="M0 138 C 110 132, 170 92, 240 90 S 350 108, 380 66" stroke="#00E5FF" strokeOpacity="0.2" strokeWidth="1" />
      <circle cx="210" cy="58" r="2" fill="#7C3AED" fillOpacity="0.6" />
      <circle cx="240" cy="90" r="2" fill="#00E5FF" fillOpacity="0.45" />
    </svg>
    <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
      <Lock className="h-3.5 w-3.5" aria-hidden /> Members only
    </p>
    <h2 className="font-heading font-black text-white text-[30px] xl:text-[34px] leading-[1.08] mt-3">
      Premium Research<br className="hidden xl:block" /> &amp; Intelligence
    </h2>
    <p className="text-[14px] text-gray-400 leading-relaxed mt-3.5 max-w-[420px]">
      Original deep dives and weekly regulatory briefings — written by the NewsLayer Intelligence Desk.
    </p>
    {isPremium ? (
      <p className="mt-5 inline-flex items-center gap-2 text-[13px] font-bold text-[#34D399]" data-testid="premium-member-badge">
        <BadgeCheck className="h-4 w-4" aria-hidden /> You have full member access
      </p>
    ) : (
      <Link to="/premium" data-testid="premium-section-cta" onClick={() => homeTrack("premium_section", "upgrade")}
        className="sparkle-btn group mt-6 inline-flex items-center gap-2 self-start rounded-lg px-5 py-2.5 text-[14px] font-bold text-white bg-gradient-to-r from-[#00A6E0] via-[#3B6CF6] to-[#7C3AED] shadow-[0_4px_24px_rgba(59,108,246,0.3)] motion-safe:transition-[filter,box-shadow] motion-safe:duration-200 hover:brightness-110 hover:shadow-[0_6px_30px_rgba(124,58,237,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
        {loggedIn ? "Upgrade to Premium" : "Explore Premium"}
        <ArrowRight className="h-4 w-4 motion-safe:transition-transform motion-safe:duration-200 group-hover:translate-x-1" aria-hidden />
      </Link>
    )}
    <div className="flex items-center gap-6 mt-auto pt-8" data-testid="premium-benefits">
      {BENEFITS.map(({ icon: Icon, tone, label }) => (
        <span key={label[0]} className="flex items-center gap-2.5">
          <Icon className={`h-[18px] w-[18px] shrink-0 ${tone}`} aria-hidden />
          <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.12em] text-gray-400 leading-[1.5]">
            {label[0]}<br />{label[1]}
          </span>
        </span>
      ))}
    </div>
  </div>
);

const PremiumValueRail = () => (
  <div className="mt-6 pt-4 border-t border-white/[0.06] flex flex-wrap items-center gap-x-5 gap-y-2"
    data-testid="premium-value-rail">
    <span className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">
      <Check className="h-3 w-3 text-[#22D3EE]" aria-hidden /> $9.99/mo <span className="text-gray-700">·</span> Cancel anytime
    </span>
    <span aria-hidden className="hidden sm:block h-3 w-px bg-white/10" />
    <span className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">
      <Check className="h-3 w-3 text-[#A78BFA]" aria-hidden /> Ad-free reading
    </span>
    <span aria-hidden className="hidden sm:block h-3 w-px bg-white/10" />
    <span className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">
      <Check className="h-3 w-3 text-[#34D399]" aria-hidden /> All premium deep dives
    </span>
  </div>
);

export const PremiumSection = ({ dives }: { dives: Article[] }) => {
  const { user } = useAuth();
  if (!dives.length) return null;
  const isPremium = !!user?.is_premium;
  return (
    <section data-testid="premium-showcase" aria-labelledby="premium-section-title"
      className="premium-aurora-frame mt-10 sm:mt-14 relative rounded-[18px] bg-black p-5 sm:p-7 overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_0_60px_-24px_rgba(59,108,246,0.45)]">
      <div aria-hidden className="absolute -top-28 -left-24 h-80 w-80 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.14), transparent 68%)" }} />
      <div aria-hidden className="absolute -bottom-24 -right-20 h-72 w-72 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.08), transparent 70%)" }} />
      <div className="relative flex flex-col gap-6 xl:grid xl:grid-cols-[minmax(0,27fr)_minmax(0,54fr)_minmax(0,19fr)] xl:gap-4">
        <span id="premium-section-title" className="sr-only">Premium Research and Intelligence</span>
        <PremiumIntro isPremium={isPremium} loggedIn={!!user} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="premium-story-grid">
          {dives.slice(0, 3).map((a, i) => (
            <PremiumStoryCard key={a.slug} a={a} index={i} isPremium={isPremium} />
          ))}
        </div>
        <RegulationRadarCard />
      </div>
      {!isPremium && <div className="relative"><PremiumValueRail /></div>}
    </section>
  );
};
